import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { errorMessagesMap } from 'src/app/constants/error-messages';
import { MainCardTypes } from 'src/app/interfaces/firstPartyTiles';
import { PetSpeciesFilterableType } from 'src/app/interfaces/pet';
import { ProductType, User } from 'src/app/interfaces/user';
import { PlanFeatureService } from 'src/app/services/common/plan-feature.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';
import { MemberShipOptionsService } from 'src/app/services/membership-options/membership-options.service';
import { UserService } from 'src/app/services/user/user.service';
import { PaymentConfigService } from 'src/app/services/payment-config/payment-config.service';

export enum PricingTableSubTypes {
  IDENTIBASEPLUS = 'IDENTIBASEPLUS',
  IDENTIALERT = 'IDENTIALERT',
}

@Component({
  selector: 'app-fixed-pricing-table',
  templateUrl: './fixed-pricing-table.component.html',
  styleUrl: './fixed-pricing-table.component.scss'
})

export class FixedPricingTableComponent implements OnInit, OnChanges{
  @Input() disabled: boolean;
  @Input() pricingPlans: any;
  @Input() viewOnly: boolean;
  @Input() public user: User;
  @Input() buttonLabel: string;
  @Input() hidePrices: boolean;
  @Input() showShortDescription: boolean;
  @Input() showInfoLine: boolean;
  @Input() showFeatures: boolean;
  @Input() showPromo: boolean;
  @Input() petName: string;
  @Input() showWideIdentiAlertCard: boolean;
  @Input() hideIcons: boolean;
  @Input() hasBuyNow: boolean;
  @Input() appPriceCardFeatureText: string[];
  @Input() appAlertPriceCardFeatureText: string[];
  @Input() showPeachBanner: boolean;
  @Input() showDescriptionList: boolean;
  /**
   * When provided, overrides the user-based card availability rules with the list from the
   * registration payment config.
   */
  @Input() availableProductIds: number[] | null = null;

  @Output() onClosePromo = new EventEmitter<string>();

  @Output() onSelect: EventEmitter<string> = new EventEmitter();
  @Output() onNavigateToProductPage: EventEmitter<string> = new EventEmitter();

  public alert: { type: string; message: string } | null = null;
  public doesTheUserHaveCat: boolean = false;
  public doesTheUserHaveDog: boolean = false;
  public productType = ProductType;
  public planIdIdentiAlert: string;
  public PricingTableSubTypes = PricingTableSubTypes;
  public promoPopupOpen: boolean;
  public doesTheUserHaveOnlyCat: boolean;
  public identibasePlusAvailable: boolean = true;
  public identiAlertAvailable: boolean = true;
  public smartTagSubAvailable: boolean = true;
  public isUpgrade: boolean;
  public isIdentiSmartTagPlanAvailable: boolean;
  public mainPlanContent = [];
  public isBrowser: boolean;
  public plan1Name: string;
  public plan2Name: string;
  public plan1YearlyPrice: string;
  public plan1DailyPrice: string;
  public plan2YearlyPrice: string;
  public plan2MonthlyPrice: string;
  public plan2DailyPrice: string;
  public brandName: string;
  public plan1Benefits: string[];
  public plan1BenefitsCat: string[];
  public plan2Benefits: string[];
  public firstPetType: number;
  public userCurrentProductType: number;
  mainCardTypes = MainCardTypes;
  closePromoPopup() {
    this.promoPopupOpen = false;
    this.onClosePromo.emit();
  }

  onCardError() {
    this.alert = { type: 'danger', message: errorMessagesMap.generic_error_message };
  }

  closeAlert() {
    this.alert = null;
  }

  constructor(
    public userService: UserService,
    public membershipOptionsService: MemberShipOptionsService,
    public planFeatureService: PlanFeatureService,
    private configService: ConfigService,
    private featureFlagService: FeatureFlagsService,
    private paymentConfigService: PaymentConfigService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.plan1Name = this.configService.getTextFromKey('1-plan-name');
    this.plan2Name = this.configService.getTextFromKey('2-plan-name');
    this.plan1YearlyPrice = this.configService.getTextFromKey('1-plan-yearly-price');
    this.plan2YearlyPrice = this.configService.getTextFromKey('2-plan-yearly-price');
    this.plan2MonthlyPrice = this.configService.getTextFromKey('2-plan-monthly-price');
    this.plan2DailyPrice = this.configService.getTextFromKey('2-plan-daily-price');
    this.brandName = this.configService.getTextFromKey('brand-name');
    this.plan1DailyPrice = this.configService.getTextFromKey('1-plan-daily-price');
    this.plan1BenefitsCat = this.configService.getListContentFromKey('1-plan-benefits-cat');
    this.plan1Benefits = this.configService.getListContentFromKey('1-plan-benefits');
    this.plan2Benefits = this.configService.getListContentFromKey('2-plan-benefits');
    this.isIdentiSmartTagPlanAvailable = this.featureFlagService.isEnabled('isIdentiSmartTagPlanAvailable');

    this.plan1YearlyPrice =
      this.paymentConfigService.getYearlyDisplayPrice(ProductType.BasicPlus) ?? this.plan1YearlyPrice;
    this.plan2YearlyPrice =
      this.paymentConfigService.getYearlyDisplayPrice(ProductType.Premium) ?? this.plan2YearlyPrice;
    this.plan2MonthlyPrice = this.paymentConfigService.getMonthlyDisplayPrice(
      ProductType.Premium,
      this.plan2MonthlyPrice
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showPromo) {
      this.showPromo = changes.showPromo.currentValue;
      this.promoPopupOpen = this.showPromo;
    }
  }

  getFeaturesFor(plan: string): any[] {
    return this.planFeatureService.getFeaturesFor(plan);
  }

  select(planId?: string): void {
    this.onSelect.emit(planId);
  }

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  ngOnInit(): void {
    if (!this.viewOnly) {
      this.planIdIdentiAlert = this.pricingPlans.identiAlertAnnual;
    }
    if (this.user) {
      this.firstPetType = this.user.pets[0].speciesFilter;
      this.userCurrentProductType = this.user.productType;
      this.doesTheUserHaveCat = !!this.user.pets.find(
        (pet) => pet.speciesFilter === PetSpeciesFilterableType.Cat
      );
      this.doesTheUserHaveDog = !!this.user.pets.find(
        (pet) => pet.speciesFilter === PetSpeciesFilterableType.Dog
      );

      if (this.doesTheUserHaveCat && !this.doesTheUserHaveDog) {
        this.doesTheUserHaveOnlyCat = true;
      }
      this.identibasePlusAvailable = this.membershipOptionsService.identiBasePlusAvailable(
        this.user
      );
      this.identiAlertAvailable = this.membershipOptionsService.identiAlertAvailable(this.user);
      this.smartTagSubAvailable = this.membershipOptionsService.smartTagSubAvailable(this.user);
    }
    if (this.doesTheUserHaveOnlyCat) {
      this.appPriceCardFeatureText = this.plan1BenefitsCat;
    } else {
      this.appPriceCardFeatureText = this.plan1Benefits;
    }
    this.appAlertPriceCardFeatureText = this.plan2Benefits;
    this.isUpgrade = this.userCurrentProductType === ProductType.Basic;

    this.applyAvailableProductIds();
  }

  /**
   * When availableProductIds is provided, it overrides the user-based card availability set above.
   * It does nothing when the input is absent, so the existing feature-flag and user-based behaviour
   * stays in place.
   */
  private applyAvailableProductIds(): void {
    const availability = this.paymentConfigService.getProductAvailability(this.availableProductIds);
    if (!availability) return;
    this.identibasePlusAvailable = availability.basicPlus;
    this.identiAlertAvailable = availability.premium;
    this.smartTagSubAvailable = availability.smartTag;
  }

  public doesTheUserHaveOnlyCats() {
    return this.doesTheUserHaveCat && !this.doesTheUserHaveDog;
  }
}
